import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SidePanel } from '../../components/layout/SidePanel';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../../components/shared/ConfirmDialog';
import { EditableGrid } from '../../components/tables/EditableGrid';
import { ROUTES } from '../../constants/routes';
import {
  addSamplesToWorkOrder,
  bulkUpdateDetailField,
  getWorkOrderById,
  getWorkOrdersByProject,
  removeWorkOrderDetails,
  updateWorkOrder,
} from '../../services/workOrderService';
import { useSignoffTeamLists } from '../../hooks/useSignoffTeamLists';
import { formatTrimNote, trimFreedAnything } from '../../utils/scheduleTrimNote';
import type { WorkOrder, WorkOrderDetailWithRelations } from '../../types/workOrder';
import { WO_STATUS_CONFIG, WO_FROZEN_FIELDS, workOrderDetailGridColumns } from '../workorders/workOrderColumns';
import { mirrorSignoffDate } from '../workorders/workOrderDetailPayload';
import { useFrozenDock } from '../../components/tables/useFrozenDock';
import { WoBulkUpdateBar } from '../workorders/WoBulkUpdateBar';
import { useWoRowSave } from '../workorders/useWoRowSave';
import { WoGridToolbarExtras } from '../workorders/WoGridToolbarExtras';
import { WoHistoryDrawer, useWoHistory } from '../workorders/WoHistoryDrawer';
import { useWorkOrderScheduleActions } from './WorkOrderScheduleActions';
import { AddSamplesModal } from '../workorders/AddSamplesModal';
import { useWoSampleUpload } from '../workorders/useWoSampleUpload';
import { ColumnSelector } from '../../components/tables/ColumnSelector';
import { useColumnPreferences } from '../../hooks/useColumnPreferences';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../auth/useAuth';

const DEFAULT_WO_FIELDS = workOrderDetailGridColumns.map((c) => c.field);
const MANDATORY_WO_FIELDS = ['SampleId', 'TestName', 'TestStatus'];

interface WorkOrdersTabContentProps {
  projectKey: string;
  projectId: string;
}

export const WorkOrdersTabContent = ({ projectKey, projectId }: WorkOrdersTabContentProps) => {
  const {
    canDeleteWorkOrder, canAddSample, canUploadCSV, isReader,
    canEditWorkOrderMeta, canManageEntities, canCheckTest, role,
  } = usePermissions();
  const { user } = useAuth();
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const navigate = useNavigate();

  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [workOrder, setWorkOrder] = useState<WorkOrder | null>(null);
  const [details, setDetails] = useState<WorkOrderDetailWithRelations[]>([]);
  // Last-server-known snapshot per row. Used to revert the grid when the
  // user cancels the review/approval reset confirm dialog.
  const serverDetailsRef = useRef<Map<string, WorkOrderDetailWithRelations>>(new Map());
  const [loading, setLoading] = useState(true);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [statusAnchor, setStatusAnchor] = useState<null | HTMLElement>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({
    open: false, message: '', severity: 'success',
  });
  const [confirmDlg, setConfirmDlg] = useState<ConfirmDialogState>(CONFIRM_INITIAL);

  /**
   * Phase 2 (qa/quote-wo-schedule-lifecycle-2026-07-11): all schedule-aware
   * behavior — banner + sync, Add/Edit Schedule, Commit Schedule, guarded
   * delete + status change, converted-from-quote toast — lives in the
   * extracted hook (600-line cap).
   */
  const {
    banner: scheduleBanner,
    scheduleButtons,
    handleStatusChange,
    handleDelete,
    deleteDialog,
    refetchImpact,
  } = useWorkOrderScheduleActions({
    workOrderId,
    workOrder,
    detailsCount: details.length,
    completedCount: details.filter((d) => d.TestStatus === 'Test Completed').length,
    projectId,
    setWorkOrder,
    setSnackbar,
    setConfirmDlg,
    onDeleted: () => navigate(ROUTES.LOCATIONS.replace(':projectId', projectId)),
  });
  // Incident WO-2026-110: the destructive action lives behind an overflow menu,
  // AWAY from the schedule-controls cluster it used to sit next to.
  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);

  const { visibleFields, columnVisibility, toggleColumn, resetDefaults } =
    useColumnPreferences('workorders', workOrderDetailGridColumns, DEFAULT_WO_FIELDS, MANDATORY_WO_FIELDS);
  // Signoff dropdowns + per-role/per-CELL readonly (RBAC.md, Phase C):
  // Reviewed By unlocks per row for skill-level-4+ holders; Approved By is
  // admin-only with the row's level-9 designates as the name list.
  const { checkerNames, approverNames, loadTeamLists, loadRowSignoffLists, decorateSignoffColumns } =
    useSignoffTeamLists(user?.displayName);
  // RBAC Phase A (2026-08-06): technicians' grid writes are whitelisted to the
  // tested-signoff trio — every other column renders read-only for them
  // (server enforces the same via validateTechnicianFieldScope). Phase C adds
  // the review pair, whose per-cell skill gate decorateSignoffColumns applies.
  const TECHNICIAN_GRID_FIELDS = useMemo(
    () => new Set(['CheckedBy', 'TestDate', 'TestStatus', 'ReviewedBy', 'ReviewDate']), []);
  const filteredWoColumns = useMemo(() =>
    workOrderDetailGridColumns
      .filter((c) => visibleFields.includes(c.field))
      .map(decorateSignoffColumns)
      // Never mark the actions column readonly — it holds the beaker (open
      // workflow) and history icons every role may use; the renderer also
      // guards this, but keep the config honest.
      .map((c) => (role === 'technician' && c.type !== 'actions'
        && !TECHNICIAN_GRID_FIELDS.has(c.field)
        ? { ...c, readonly: true }
        : c)),
    [visibleFields, decorateSignoffColumns, role, TECHNICIAN_GRID_FIELDS],
  );
  // BUG-2026-0159: freeze the leading Location/Sample/Depth columns. The
  // frozen fields all sit at the head of the column config, so the count of
  // visible ones equals the leading-run length EditableGrid expects. Users can
  // undock the pane (pin toggle) — useful on smaller screens.
  const { docked, toggleDock } = useFrozenDock('workorders');
  // Audit-trail drawer (mig 110): whole-WO via the toolbar History button,
  // single row via the per-row history icon.
  const history = useWoHistory();
  const handleRowHistory = useCallback((row: Record<string, unknown>) => {
    history.open({
      detailKey: String(row.WorkOrderDetailKey),
      label: [row.SampleId, row.SpecimenRef].filter(Boolean).join(' / ')
        + (row.TestName ? ` — ${row.TestName}` : ''),
    });
  }, [history]);
  const frozenColumnCount = useMemo(
    () => filteredWoColumns.filter((c) => WO_FROZEN_FIELDS.includes(c.field)).length,
    [filteredWoColumns],
  );

  const loadWorkOrder = useCallback(async () => {
    if (!workOrderId) return;
    setLoading(true);
    const result = await getWorkOrderById(workOrderId);
    setWorkOrder(result.workOrder);
    setDetails(result.details);
    const snap = new Map<string, WorkOrderDetailWithRelations>();
    result.details.forEach((d) => snap.set(String(d.WorkOrderDetailKey), { ...d }));
    serverDetailsRef.current = snap;
    setLoading(false);
    await loadTeamLists();
    // Phase C: skill-scoped Reviewed By / Approved By lists — one fetch per
    // distinct skill signature across the loaded rows (best-effort).
    void loadRowSignoffLists(result.details as unknown as Record<string, unknown>[]);
  }, [workOrderId, loadTeamLists, loadRowSignoffLists]);

  useEffect(() => {
    const load = async () => {
      const wos = await getWorkOrdersByProject(projectKey);
      setWorkOrders(wos);
      if (!workOrderId && wos.length > 0) {
        const path = ROUTES.WORK_ORDER_DETAIL
          .replace(':projectId', projectId)
          .replace(':workOrderId', String(wos[0].WorkOrderKey));
        navigate(path, { replace: true });
      }
      if (!workOrderId && wos.length === 0) setLoading(false);
    };
    load();
  }, [projectKey, projectId, workOrderId, navigate]);

  useEffect(() => { loadWorkOrder(); }, [loadWorkOrder]);

  const sidePanelItems = useMemo(() =>
    workOrders.map((wo) => {
      const status = wo.WorkOrderStatus || 'Scheduled';
      const isSelected = String(wo.WorkOrderKey) === workOrderId;
      const secondary = isSelected && details.length > 0
        ? `${status} · ${details.length} sample${details.length !== 1 ? 's' : ''}`
        : status;
      return {
        key: String(wo.WorkOrderKey),
        label: wo.WorkOrderName ? `${wo.WorkOrderId} — ${wo.WorkOrderName}` : wo.WorkOrderId,
        secondary,
        statusColor: WO_STATUS_CONFIG[status]?.dot,
      };
    }), [workOrders, workOrderId, details.length]);

  const handleSelectWO = (key: string) => {
    navigate(ROUTES.WORK_ORDER_DETAIL.replace(':projectId', projectId).replace(':workOrderId', key));
  };

  const handleNewWO = () => {
    navigate(ROUTES.CREATE_WORK_ORDER.replace(':projectId', projectId));
  };

  const handleRowChange = (rowIndex: number, field: string, value: unknown) => {
    setDetails((prev) => {
      const next = [...prev];
      // Signoff person ⇄ date mirroring lives in mirrorSignoffDate (BUG-2026-0011).
      const row = mirrorSignoffDate({ ...next[rowIndex], [field]: value }, field, value);
      next[rowIndex] = row as unknown as typeof next[number];
      return next;
    });
  };

  // Row save machinery (optimistic save, 409 reset confirm, OK-only error
  // dialog) — extracted to useWoRowSave (600-line cap).
  const { handleRowSave, handleConfirmClose } = useWoRowSave({
    details, setDetails, serverDetailsRef, confirmDlg, setConfirmDlg, setSnackbar,
  });

  const handleRowAction = useCallback((row: Record<string, unknown>) => {
    if (!workOrderId) return;
    navigate(ROUTES.TEST_PARAMETERS.replace(':projectId', projectId).replace(':workOrderId', workOrderId)
      .replace(':workOrderDetailKey', String(row.WorkOrderDetailKey)));
  }, [projectId, workOrderId, navigate]);

  const handleAddSamples = useCallback(async (sampleKeys: string[], testKeys: string[], specimenCounts?: Record<string, number>) => {
    if (!workOrderId) return;
    try {
      await addSamplesToWorkOrder(workOrderId, sampleKeys, testKeys, specimenCounts);
      setAddModalOpen(false);
      await loadWorkOrder();
      setSnackbar({ open: true, message: 'Samples added', severity: 'success' });
    } catch { setSnackbar({ open: true, message: 'Failed to add samples', severity: 'error' }); }
  }, [workOrderId, loadWorkOrder]);

  const handleRemoveSelected = useCallback(() => {
    if (selectedKeys.length === 0) return;
    const count = selectedKeys.length;
    setConfirmDlg({
      open: true,
      title: 'Remove Samples',
      message: `Remove ${count} sample${count > 1 ? 's' : ''} from this work order?`,
      confirmLabel: 'Remove',
      confirmColor: 'error',
      onConfirm: async () => {
        try {
          // G2-4: surface schedule drift + refresh the banner so the
          // "Out of sync" chip appears without a full page reload.
          const result = await removeWorkOrderDetails(selectedKeys);
          setSelectedKeys([]);
          await loadWorkOrder();
          // G2-2: the removal released the tests' future schedule days too.
          const freed = trimFreedAnything(result.scheduleTrim)
            ? ` Schedule freed: ${formatTrimNote(result.scheduleTrim)}.`
            : '';
          setSnackbar(result.scheduledTasks > 0
            ? { open: true, message: `${count} sample${count > 1 ? 's' : ''} removed - ${result.scheduledTasks} scheduled task(s) now out of sync. Use "Sync now" above.${freed}`, severity: 'warning' }
            : { open: true, message: `${count} sample${count > 1 ? 's' : ''} removed${freed}`, severity: 'success' });
          await refetchImpact();
        } catch { setSnackbar({ open: true, message: 'Failed to remove samples', severity: 'error' }); }
      },
    });
  }, [selectedKeys, loadWorkOrder, refetchImpact]);

  const handleRowDelete = useCallback((row: Record<string, unknown>) => {
    const key = row.WorkOrderDetailKey as string;
    const sampleId = row.SampleId as string || 'this sample';
    setConfirmDlg({
      open: true,
      title: 'Remove Sample',
      message: `Remove ${sampleId} from this work order?`,
      confirmLabel: 'Remove',
      confirmColor: 'error',
      onConfirm: async () => {
        try {
          // G2-4: same drift cue + banner refresh as the bulk removal above.
          const result = await removeWorkOrderDetails([key]);
          await loadWorkOrder();
          // G2-2: the removal released the test's future schedule days too.
          const freed = trimFreedAnything(result.scheduleTrim)
            ? ` Schedule freed: ${formatTrimNote(result.scheduleTrim)}.`
            : '';
          setSnackbar(result.scheduledTasks > 0
            ? { open: true, message: `${sampleId} removed - ${result.scheduledTasks} scheduled task(s) now out of sync. Use "Sync now" above.${freed}`, severity: 'warning' }
            : { open: true, message: `${sampleId} removed${freed}`, severity: 'success' });
          await refetchImpact();
        } catch { setSnackbar({ open: true, message: 'Failed to remove sample', severity: 'error' }); }
      },
    });
  }, [loadWorkOrder, refetchImpact]);

  /** Bulk field update for the selected rows (ported from the retired orphan
   *  WorkOrderDetail.tsx — the bar was stranded on an unrouted page). */
  const handleBulkUpdate = useCallback(async (field: string, value: string) => {
    if (selectedKeys.length === 0) return;
    try {
      const result = await bulkUpdateDetailField(selectedKeys, field, value);
      setDetails((prev) => prev.map((d) =>
        selectedKeys.includes(String(d.WorkOrderDetailKey)) ? { ...d, [field]: value } : d,
      ));
      setSnackbar({ open: true, message: `Updated ${result.updated} rows`, severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Bulk update failed', severity: 'error' });
    }
  }, [selectedKeys]);

  // Upload Samples & Tests (2026-09-10): CSV / intake workbook / KeyLAB form →
  // reconcile against the WO (placeholders replaced) → apply. Extracted hook.
  const { openUploadModal, uploadDialogs } = useWoSampleUpload({
    workOrderId, projectKey, projectId, navigate, setSnackbar, loadWorkOrder, refetchImpact,
  });

  const existingSampleKeys = useMemo(() => details.map((d) => d.SampleKey), [details]);

  return (
    <Box sx={{ display: 'flex', flex: 1, minHeight: 0, gap: 0 }}>
      <SidePanel
        title="Work Orders" items={sidePanelItems}
        selectedKey={workOrderId || ''} onSelect={handleSelectWO}
        onAdd={handleNewWO} addLabel="+ New Work Order" searchPlaceholder="Search work orders..."
      />
      <Box sx={{ flex: 1, overflow: 'auto', pl: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : !workOrder ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {workOrders.length === 0 ? 'No work orders yet.' : 'Select a work order from the left panel.'}
            </Typography>
            {workOrders.length === 0 && canUploadCSV && (
              <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
                <Button variant="outlined" onClick={openUploadModal}>
                  Upload Samples &amp; Tests
                </Button>
              </Box>
            )}
          </Box>
        ) : (
          <>
            {/* FEAT-2026-0012 — persistent banner when a plan is attached to this WO. */}
            {scheduleBanner}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="h5" fontWeight={600}>{workOrder.WorkOrderId}</Typography>
                {workOrder.WorkOrderStatus && (
                  <>
                    <Chip
                      label={WO_STATUS_CONFIG[workOrder.WorkOrderStatus]?.label || workOrder.WorkOrderStatus}
                      color={WO_STATUS_CONFIG[workOrder.WorkOrderStatus]?.color || 'default'}
                      size="small" variant="filled"
                      onClick={canEditWorkOrderMeta ? (e) => setStatusAnchor(e.currentTarget) : undefined}
                      sx={{ cursor: canEditWorkOrderMeta ? 'pointer' : 'default' }}
                    />
                    <Menu anchorEl={statusAnchor} open={Boolean(statusAnchor)} onClose={() => setStatusAnchor(null)}>
                      {Object.entries(WO_STATUS_CONFIG).map(([key, cfg]) => (
                        <MenuItem key={key} selected={key === workOrder.WorkOrderStatus}
                          onClick={async () => {
                            setStatusAnchor(null);
                            if (key !== workOrder.WorkOrderStatus) {
                              // P2-4: Cancelled/Completed transitions release/trim
                              // the schedule (committed work is confirm-guarded).
                              await handleStatusChange(key, cfg.label);
                            }
                          }}>
                          <Chip label={cfg.label} color={cfg.color} size="small" variant="filled" sx={{ mr: 1 }} /> {cfg.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
                <TextField size="small" placeholder="Add a descriptive name..."
                  disabled={!canEditWorkOrderMeta}
                  value={workOrder.WorkOrderName || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setWorkOrder((prev) => prev ? { ...prev, WorkOrderName: val } : prev);
                    setWorkOrders((prev) => prev.map((wo) => String(wo.WorkOrderKey) === workOrderId ? { ...wo, WorkOrderName: val } : wo));
                  }}
                  onBlur={async (e) => {
                    if (!workOrderId) return;
                    try {
                      await updateWorkOrder(workOrderId, { WorkOrderName: e.target.value || null });
                    } catch {
                      setSnackbar({ open: true, message: 'Failed to save work order name', severity: 'error' });
                    }
                  }}
                  sx={{
                    minWidth: 220, position: 'relative', zIndex: 1,
                    // WO-name affordance: italic + muted default so the whole
                    // cell reads as "click to edit"; on hover the value pops,
                    // on focus it becomes a normal editing input.
                    '& .MuiInputBase-input': {
                      fontSize: '0.85rem',
                      fontStyle: 'italic',
                      color: 'text.secondary',
                      transition: 'color 150ms',
                    },
                    '&:hover .MuiInputBase-input': { color: 'text.primary' },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderStyle: 'dashed',
                        borderColor: (theme) => alpha(theme.palette.primary.main, 0.32),
                      },
                      '&:hover fieldset': {
                        borderColor: (theme) => alpha(theme.palette.primary.main, 0.55),
                      },
                      '&.Mui-focused fieldset': {
                        borderStyle: 'solid',
                        borderColor: 'primary.main',
                      },
                      '&.Mui-focused .MuiInputBase-input': {
                        fontStyle: 'normal',
                        color: 'text.primary',
                      },
                    },
                  }}
                />
                {/* External caption + labelless TextField. MUI size="small"
                    notched-outline's <legend> is shorter than the shrunk label
                    renders, clipping the label's top. See LESSONS-LEARNED 2026-04-20. */}
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 130 }}>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1, mb: 0.25, ml: 0.5 }}
                  >
                    Lab
                  </Typography>
                  <TextField
                    size="small"
                    select
                    disabled={!canEditWorkOrderMeta}
                    value={(workOrder as any)?.LabLocation || 'Fredericton'}
                    onChange={async (e) => {
                      if (!workOrderId) return;
                      const v = e.target.value;
                      setWorkOrder((prev) => prev ? { ...prev, LabLocation: v } as any : prev);
                      try { await updateWorkOrder(workOrderId, { LabLocation: v } as any); }
                      catch { /* ignore */ }
                    }}
                    SelectProps={{ native: true }}
                  >
                    <option value="Fredericton">Fredericton</option>
                    <option value="Vancouver">Vancouver</option>
                  </TextField>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {selectedKeys.length > 0 && canManageEntities && <Button variant="outlined" color="error" onClick={handleRemoveSelected}>Remove Selected ({selectedKeys.length})</Button>}
                {/* Add/Edit Schedule + Commit Schedule (WorkOrderScheduleActions). */}
                {scheduleButtons}
                {canAddSample && <Button variant="outlined" onClick={() => setAddModalOpen(true)}>+ Add Tests</Button>}
                {canUploadCSV && <Button variant="outlined" onClick={openUploadModal}>Upload Samples</Button>}
                <WoGridToolbarExtras docked={docked} onToggleDock={toggleDock} onHistory={() => history.open()} />
                <ColumnSelector columns={columnVisibility} onToggle={toggleColumn} onReset={resetDefaults} />
                {canDeleteWorkOrder && (
                  <>
                    <Button
                      variant="text" size="small" onClick={(e) => setMoreAnchor(e.currentTarget)}
                      aria-label="More actions"
                      sx={{ minWidth: 32, px: 0.5, color: 'text.secondary', fontSize: '1.1rem' }}
                    >
                      ⋮
                    </Button>
                    <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}>
                      <MenuItem
                        onClick={() => { setMoreAnchor(null); handleDelete(); }}
                        sx={{ color: 'error.main', fontSize: '0.85rem' }}
                      >
                        Delete work order…
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </Box>
            </Box>
            <EditableGrid
              columns={filteredWoColumns} data={details as unknown as Record<string, unknown>[]}
              onRowChange={handleRowChange} onRowSave={handleRowSave}
              onSelectionChange={setSelectedKeys} keyField="WorkOrderDetailKey"
              allowAdd={false} onAddRow={() => {}} allowDelete={false} onDelete={() => {}}
              onAction={handleRowAction} onRowClick={handleRowAction}
              // Removing a test from the WO is admin-only (server:
              // POST /details/bulk-delete requireRole('admin')) — omitting the
              // handler hides the trash icon for everyone else.
              onRowDelete={canManageEntities ? handleRowDelete : undefined}
              onRowHistory={handleRowHistory}
              readOnly={isReader}
              enableColumnFilters
              freezeColumns={docked ? frozenColumnCount : 0}
              stickyHeader
              showScrollArrows
              autoSaveCells
            />
            {!isReader && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, px: 0.5 }}>
                Dropdown and date changes save automatically. Typed text (instructions) saves when
                you click elsewhere — rows with unsaved changes are tinted amber.
              </Typography>
            )}
          </>
        )}
      </Box>
      <WoHistoryDrawer workOrderKey={workOrder?.WorkOrderKey ? String(workOrder.WorkOrderKey) : workOrderId}
        workOrderLabel={workOrder?.WorkOrderId ?? 'Work order'} target={history.target} onClose={history.close} />
      <AddSamplesModal open={addModalOpen} onClose={() => setAddModalOpen(false)} onAdd={handleAddSamples}
        projectKey={projectKey} existingSampleKeys={existingSampleKeys} />
      {uploadDialogs}
      <Snackbar open={snackbar.open} autoHideDuration={snackbar.severity === 'success' ? 2000 : 4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((p) => ({ ...p, open: false }))} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
      <ConfirmDialog state={confirmDlg} onClose={handleConfirmClose} />
      {deleteDialog}
      {/* Gated to match PUT /workorders/details/bulk-update-field: admins get all
          fields; technicians get the check-step trio (bar filters per role). */}
      {selectedKeys.length > 0 && (canEditWorkOrderMeta || canCheckTest) && (
        <WoBulkUpdateBar
          selectedCount={selectedKeys.length}
          checkerNames={checkerNames}
          approverNames={approverNames}
          onApply={handleBulkUpdate}
        />
      )}
    </Box>
  );
};
